import { useContext, useState } from "react"
import { UserContext } from "../../../context/userContext"

// Helpers
import cloudFunctionsEndpoint from "../../../config/cloudFunctionsEndpoint"
import { LogError } from "../../../helpers/logger"
import { normalizePayload } from "../../../helpers/dateNormalization"
import { callCloudFunction } from "../../../helpers/cloudFunctionRequests"

const useFormSubmit = () => {
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(false)
  const [committedBeforeError, setCommittedBeforeError] = useState(false)
  const [message, setMessage] = useState("")
  const [transactionId, setTransactionId] = useState("")
  const [activeStep, setActiveStep] = useState(0)
  const userCtx = useContext(UserContext)

  const initSubmit = async (rawPayload, fieldDefs) => {
    // Normalize dates BEFORE sending anything
    // This avoids timezone issues and inconsistent date formats
    const normalizedPayload = normalizePayload(rawPayload, fieldDefs)

    // Reset state for new submission
    setActiveStep(0)
    setSubmitting(true)
    setErr(false)
    setCommittedBeforeError(false)
    setMessage("")

    // For error logging context
    let _form_id = ""
    let _function_name = ""
    let _transaction_id = ""
    let _stage = ""

    try {
      const EDIT_MODE = Boolean(normalizedPayload?.idStamp)

      // =====================================================
      // Step 0: Validate Form
      // =====================================================
      _stage = "validation" // Used for error logging context
      _function_name = "formValidate" // Used for error logging context

      const fetchValidation = await callCloudFunction(
        "validateForm",
        normalizedPayload,
        userCtx.tokens.getMainToken()
      )

      const {
        isValidForm,
        form_id,
        transaction_id,
        msg: msgValidation,
      } = fetchValidation.data

      setTransactionId(transaction_id)
      setMessage(msgValidation)

      _form_id = form_id // Used for error logging context
      _transaction_id = transaction_id // Used for error logging context

      if (!isValidForm) {
        setErr(true)
        setMessage(msgValidation)
        setSubmitting(false)
        return
      }

      // Attach server-generated identifiers
      normalizedPayload.form_id = form_id
      normalizedPayload.transaction_id = transaction_id

      await new Promise((resolve) => setTimeout(resolve, 900))

      // =====================================================
      // Step 1: Submit Form
      // =====================================================
      setActiveStep(1)
      _stage = "submission" // Used for error logging context

      let fetchSubmission = {}
      if (EDIT_MODE) {
        _function_name = "formEdit" // Used for error logging context

        fetchSubmission = await callCloudFunction(
          "formEdit",
          normalizedPayload,
          userCtx.tokens.getMainToken()
        )
      } else {
        _function_name = "formSubmit" // Used for error logging context

        fetchSubmission = await callCloudFunction(
          "formSubmit",
          normalizedPayload,
          userCtx.tokens.getMainToken()
        )

      }

      // Clean up staged submission after successful submission/edit
      try {
        await callCloudFunction("deleteStagedFormSubmission", { form_id }, userCtx.tokens.getMainToken())
      } catch (cleanupError) {
        LogError(`Failed to clean up staged submission (form_id: ${form_id}):`, cleanupError)
      }

      const { msg: msgSubmit } = fetchSubmission.data
      setMessage(msgSubmit)

      await new Promise((resolve) => setTimeout(resolve, 1000))

      // =====================================================
      // Step 2: Confirmation
      // =====================================================
      setActiveStep(2)
      _stage = "confirmation" // Used for error logging context

      await new Promise((resolve) => setTimeout(resolve, 2000))

      // =====================================================
      // Step 3: Complete
      // =====================================================
      setActiveStep(3)
      setSubmitting(false)
    } catch (error) {
      LogError("Form submission error:", error)

      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        "An unexpected error occurred."

      setCommittedBeforeError(!!error?.response?.data?.dataCommitted)
      setErr(true)
      setMessage(errorMessage)
      setActiveStep(3)

      // Log submission payload and error details to cloud function for debugging
      await errorLogPayload(
        cloudFunctionsEndpoint(),
        _function_name,
        _form_id,
        _transaction_id,
        _stage,
        normalizedPayload,
        errorMessage,
        userCtx,
      )
    }
  }

  return {
    submitting,
    err,
    committedBeforeError,
    message,
    transactionId,
    activeStep,
    initSubmit,
  }
}

async function errorLogPayload(
  endpoint,
  function_name,
  form_id,
  transaction_id,
  stage,
  normalizedPayload,
  errorMessage,
  userCtx,
) {
  try {
    const errorLogBody = {
      // Submission context
      function_name: `client_${function_name}`,
      stamp_id: normalizedPayload?.idStamp || "",
      template_id: normalizedPayload?.idTemplate || "",
      form_id: form_id || "",
      transaction_id: transaction_id || "",
      stage: stage || "",
      error: errorMessage || "",
      payload: normalizedPayload,

      // Organizational context
      enterprise_id: userCtx?.session?.user?.enterprise_id || "",
      enterprise_alias: userCtx?.session?.user?.enterprise_alias || "",
      organization_id: userCtx?.session?.user?.organization_id || "",
      organization_alias: userCtx?.session?.user?.organization_alias || "",

      // User context
      staff_id: userCtx?.session?.user?.staff_id || "",
      staff_alias: userCtx?.session?.user?.staff_alias || "",

      // Participant context (if applicable)
      participant_id: userCtx?.participant_id || "",
      participant_alias: userCtx?.participant_alias || "",
      participant_membership_id: userCtx?.participant_membership_id || "",
    }

    // Log error details to cloud function for debugging
    await callCloudFunction("logPayload", errorLogBody, userCtx.tokens.getMainToken())
  } catch (error) {
    LogError("Error logging submission payload:", error)
  }
}

export default useFormSubmit
